import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ProgressBar, StatusBadge } from "@/components/common/ui-bits";
import { money, monthLong } from "@/lib/format";

export function BudgetAlerts({ alerts, month }) {
  if (!alerts.length) return null;
  return (
    <div className="space-y-2 mb-6 fade-up" data-testid="dashboard-budget-alerts">
      {alerts.map((a) => {
        const over = a.status === "over";
        return (
          <div key={a.category_id} data-testid={`budget-alert-${a.category_id}`}
            className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border px-4 py-3 text-sm ${over ? "border-rose-200 bg-rose-50 text-rose-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
            <AlertTriangle className={`h-4 w-4 shrink-0 ${over ? "text-rose-500" : "text-amber-500"}`} />
            <span className="font-semibold">{over ? "Over budget" : "Approaching budget"}</span>
            <span className="min-w-0">{a.name}: {money(a.actual_cents)} of {money(a.budget_cents)} ({a.percent_used}%) in {monthLong(month)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function OverallBudgetCard({ overall, month }) {
  return (
    <div className="surface p-5 sm:p-6 h-full fade-up" data-testid="dashboard-overall-budget">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="font-heading text-lg font-semibold">Monthly spending target</h3>
        <StatusBadge status={overall.status} testId="dashboard-overall-status" />
      </div>
      <p className="text-xs text-muted-foreground mb-5">{monthLong(month)}</p>
      {overall.budget_cents == null ? (
        <div className="text-sm text-muted-foreground">
          No overall target set. <Link to="/budgets" className="font-semibold text-sky-700 underline" data-testid="dashboard-set-target-link">Set one in Budgets</Link>
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between text-sm mb-2">
            <span className="font-num font-semibold text-lg">{money(overall.actual_cents)}</span>
            <span className="text-muted-foreground">of <span className="font-num">{money(overall.budget_cents)}</span></span>
          </div>
          <ProgressBar percent={overall.percent_used} status={overall.status} />
          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
            {overall.status === "ok" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
            {overall.percent_used ?? 0}% used · {money(overall.remaining_cents)} {overall.remaining_cents >= 0 ? "left" : "over"}
          </div>
        </>
      )}
      <Link to="/budgets" className="mt-6 inline-block text-sm font-semibold text-sky-700 hover:underline" data-testid="dashboard-budgets-link">View all budgets →</Link>
    </div>
  );
}
